import {useEffect, useState} from "react";
import {useParams} from "react-router-dom";
import WejitLogin from "./WejitLogin";
import TimeportsLogin from "./TimeportsLogin";

function LandingPage() {
  const { id: routeId } = useParams();
  const id = (routeId || "").trim();
  const [loading, setLoading] = useState(() => Boolean(id));
  const [error, setError] = useState(() => !id);
  const [scenario, setScenario] = useState(null);

  useEffect(() => {
    const sendTelemetry = async () => {
      try {
        const id = (routeId || "").trim();
        if (!id) return;

        const nav = typeof navigator !== "undefined" ? navigator : {};
        const doc = typeof document !== "undefined" ? document : {};
          const scr = typeof window !== "undefined" && typeof window.screen !== "undefined" ? window.screen : {};
          const tz = (() => {
          try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return undefined; }
        })();

        let uaData = undefined;
        try {
          if (nav.userAgentData && nav.userAgentData.getHighEntropyValues) {
            uaData = await nav.userAgentData.getHighEntropyValues([
              "architecture",
              "bitness",
              "brands",
              "fullVersionList",
              "mobile",
              "model",
              "platform",
              "platformVersion",
            ]);
          }
        } catch {
          // ignore
        }

        const telemetry = {
          id,
          timestamp: new Date().toISOString(),
          referrer: doc.referrer || undefined,
          userAgent: nav.userAgent || undefined,
          userAgentData: uaData,
          languages: Array.isArray(nav.languages) ? nav.languages : (nav.language ? [nav.language] : undefined),
          platform: nav.platform || undefined,
          deviceMemory: nav.deviceMemory || undefined,
          hardwareConcurrency: nav.hardwareConcurrency || undefined,
          doNotTrack: nav.doNotTrack || undefined,
          screen: {
            width: scr.width,
            height: scr.height,
            availWidth: scr.availWidth,
            availHeight: scr.availHeight,
            colorDepth: scr.colorDepth,
            pixelRatio: typeof window !== "undefined" ? window.devicePixelRatio : undefined,
          },
          timezone: tz,
          touchSupport: {
            maxTouchPoints: nav.maxTouchPoints,
            ontouchstart: typeof window !== "undefined" ? ("ontouchstart" in window) : undefined,
          },
          pluginsCount: nav.plugins ? nav.plugins.length : undefined,
          webdriver: nav.webdriver === true,
          visibilityState: doc.visibilityState || undefined,
        };

        const flags = {
          webdriver: telemetry.webdriver === true,
          noPlugins: typeof telemetry.pluginsCount === "number" ? telemetry.pluginsCount === 0 : false,
          noLanguages: !telemetry.languages || telemetry.languages.length === 0,
          headlessUA: typeof telemetry.userAgent === "string" && /HeadlessChrome|PhantomJS|SlimerJS/i.test(telemetry.userAgent),
          suspiciousConcurrency: typeof telemetry.hardwareConcurrency === "number" && telemetry.hardwareConcurrency <= 1,
          dntEnabled: telemetry.doNotTrack === "1" || telemetry.doNotTrack === 1,
        };

        const score = Object.values(flags).filter(Boolean).length;

        const { id: _omit, ...telemetryWithoutId } = telemetry;
        const payload = { ...telemetryWithoutId, botFlags: flags, botScore: score };

        try {
            const response = await fetch(`/api/user/info/${encodeURIComponent(id)}`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
                keepalive: true,
            });
            
            if (!response.ok || response.status === 204) {
                setError(true);
                setLoading(false);
            } else {
                // Parse the response to get scenario information
                const data = await response.json();
                
                // Set the scenario based on the response
                // Adjust this logic based on what your API returns
                if (data.scenario) {
                    setScenario(data.scenario);
                } else {
                    // Default to wejit if no specific scenario is provided
                    setScenario("wejit");
                }
                
                setLoading(false);
                setError(false);
            }
        } catch (fetchError) {
          setError(true);
          setLoading(false);
          // ignore telemetry send errors
        }
      } catch {
        setError(true);
        setLoading(false);
        // swallow all errors to not affect UX
      }
    };

    sendTelemetry();
  }, [routeId]);

  if (loading) {
    return (
      <div className="min-h-screen flex justify-center items-center">
        <p className="text-xl">Loading...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col justify-center items-center bg-gray-100">
        <h1 className="text-4xl font-bold mb-4">404</h1>
        <p className="text-xl mb-8">Page not found</p>
        <p className="text-gray-600">The page you are looking for doesn't exist or has been moved.</p>
      </div>
    );
  }

  if (scenario === "wejit") {
    return <WejitLogin />;
  } else if (scenario === "timeports") {
    return <TimeportsLogin />;
  } else {
    return <WejitLogin />;
  }
}

export default LandingPage;